import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { rentPaymentFailureFeeSettingsRepository } from '~/repositories/settings/rentPaymentFailureFeeSettingsRepository';
import type { Accessor } from 'solid-js';
import type {
  RentPaymentFailureFeeSettings,
  RentPaymentFailureFeeSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

interface RentPaymentFailureFeeSettingsContextValue {
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;

  addRentPaymentFailureFeeSettingsByLevel: (level: string, levelId: string, payload: RentPaymentFailureFeeSettings) => Promise<void>;
  deleteRentPaymentFailureFeeSettingsByLevel: (level: string, levelId: string) => Promise<void>;

  updateRentPaymentFailureFeeSettings: (payload: RentPaymentFailureFeeSettings) => Promise<RentPaymentFailureFeeSettings | undefined>;
  getRentPaymentFailureFeeSettings: () => Promise<RentPaymentFailureFeeSettings | undefined>;
  deleteRentPaymentFailureFeeSettings: () => Promise<void>;

  getAllRentPaymentFailureFeeSettings: () => Promise<
    RentPaymentFailureFeeSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
  >;

  allRentPaymentFailureFeeSettings: Accessor<RentPaymentFailureFeeSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined>;
  rentPaymentFailureFeeSettings: Accessor<RentPaymentFailureFeeSettings | undefined>;
}

export const [RentPaymentFailureFeeSettingsProvider, useRentPaymentFailureFeeSettings] =
  createMagicDoorContext<RentPaymentFailureFeeSettingsContextValue>('RentPaymentFailureFeeSettings', () => {
    const repo = new rentPaymentFailureFeeSettingsRepository();
    const [rentPaymentFailureFeeSettings, setRentPaymentFailureFeeSettings] = createSignal<RentPaymentFailureFeeSettings | undefined>(
      undefined
    );
    const [allRentPaymentFailureFeeSettings, setAllRentPaymentFailureFeeSettings] = createSignal<
      RentPaymentFailureFeeSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    >(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const getRentPaymentFailureFeeSettings = async (): Promise<RentPaymentFailureFeeSettings | undefined> => {
      setIsLoading(true);

      try {
        const result = await repo.getRentPaymentFailureFeeSettings();
        setRentPaymentFailureFeeSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getAllRentPaymentFailureFeeSettings = async (): Promise<
      RentPaymentFailureFeeSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    > => {
      setIsLoading(true);
      try {
        const result = await repo.getAllRentPaymentFailureFeeSettings();
        setAllRentPaymentFailureFeeSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const updateRentPaymentFailureFeeSettings = async (
      payload: RentPaymentFailureFeeSettings
    ): Promise<RentPaymentFailureFeeSettings | undefined> => {
      setIsLoading(true);

      try {
        const result = await repo.updateRentPaymentFailureFeeSettings(payload);
        setRentPaymentFailureFeeSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const deleteRentPaymentFailureFeeSettings = async (): Promise<void> => {
      setIsLoading(true);

      try {
        await repo.deleteRentPaymentFailureFeeSettings();
        setRentPaymentFailureFeeSettings(undefined);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const addRentPaymentFailureFeeSettingsByLevel = async (level: string, levelId: string, payload: RentPaymentFailureFeeSettings) => {
      setIsLoading(true);
      try {
        await repo.addRentPaymentFailureFeeSettingsByLevel(level, levelId, payload);
        await getAllRentPaymentFailureFeeSettings();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const deleteRentPaymentFailureFeeSettingsByLevel = async (level: string, levelId: string) => {
      setIsLoading(true);
      try {
        await repo.deleteRentPaymentFailureFeeSettingsByLevel(level, levelId);
        await getAllRentPaymentFailureFeeSettings();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    return {
      isLoading,
      error,
      updateRentPaymentFailureFeeSettings,
      getRentPaymentFailureFeeSettings,
      deleteRentPaymentFailureFeeSettings,
      getAllRentPaymentFailureFeeSettings,
      addRentPaymentFailureFeeSettingsByLevel,
      deleteRentPaymentFailureFeeSettingsByLevel,
      rentPaymentFailureFeeSettings,
      allRentPaymentFailureFeeSettings,
    };
  });
